import { CustomRule, Prisma, ReportStatus } from "@prisma/client";
import { StudentBasic } from "./admin/students";
import { OrderedRulesTemplates } from "./rules";
import { SkillsBasic, ScoreRangeValues } from "./scores";

export interface IReport {
  version: number;
  assessment?: {
    completedAt: string;
  };
  status: ReportStatus;
  student: StudentBasic;
  template: MindPrintProfileTemplate;
}

export interface MindPrintProfileReport extends IReport {
  skills: SkillsBasic;
  customRules: CustomRule[] | undefined;
  rules:
    | {
        strengths: Array<OrderedRulesTemplates>;
        needs: Array<OrderedRulesTemplates>;
        recommendations: Array<OrderedRulesTemplates>;
      }
    | undefined;
  template: MindPrintProfileTemplate;
}

export interface MindPrintProfileTemplate {
  disclaimer: ReportContent;
  nextSteps: NextStepsContent;
  performanceBySkill: {
    content: ReportContent;
    speed: {
      content: ReportContent;
      visualMotorSpeed: ReportSkillContent;
      processingSpeed: ReportSkillContent;
    };
    executiveFunctions: {
      content: ReportContent;
      attention: ReportSkillContent;
      workingMemory: ReportSkillContent;
      flexibleThinking: ReportSkillContent;
    };
    complexReasoning: {
      content: ReportContent;
      verbalReasoning: ReportSkillContent;
      abstractReasoning: ReportSkillContent;
      spatialPerception: ReportSkillContent;
    };
    memory: {
      content: ReportContent;
      verbalMemory: ReportSkillContent;
      visualMemory: ReportSkillContent;
    };
  };
  translateResults: {
    content: ReportContent;
    improveAcademicPerformance: ReportContent;
    supportCognitiveSkills: ReportContent;
  };
  understandAnxietyMood: ReportContent;
  sharingResults: ReportContent;
}

export interface ReportContent {
  title: string;
  htmlContent: Array<string>;
}

export interface NextStepsContent {
  title: string;
  htmlContent: Array<string>;
  highStrength: {
    name: string;
    link: string;
  }[];
  mediumStrength: {
    name: string;
    link: string;
  }[];
  highExpected: {
    name: string;
    link: string;
  }[];
  mediumExpected: {
    name: string;
    link: string;
  }[];
  lowExpected: {
    name: string;
    link: string;
  }[];
  mediumWeakness: {
    name: string;
    link: string;
  }[];
  lowWeakness: {
    name: string;
    link: string;
  }[];
  invalid: {
    name: string;
    link: string;
  }[];
}

export enum ReasoningGroups {
  strongerVerbal = "2 - Stronger Verbal",
  strongerVisualAbstract = "1 - Stronger Visual-Abstract",
}

export enum MemoryGroups {
  strongerVerbal = "2 - Stronger Verbal",
  strongerVisual = "1 - Stronger Visual",
}
export interface ReportSkillContent {
  content: ReportContent;
  summary: ReportSkillSummary;
}

export interface ReportSkillSummary extends ReportContent {
  scoreRange: ScoreRangeValues;
}

const reportFlag = Prisma.validator<Prisma.Report$flagsArgs>()({
  select: {
    reportId: true,
    flagType: true,
    description: true,
  },
});

type ReportFlagType = Prisma.ReportFlagGetPayload<typeof reportFlag>;

export interface IReportFlag extends ReportFlagType {}
